import { runtimeConfig } from '@/shared/config'

// Зон две, и путать их — тот самый дефект.
//
// Зона ОРГАНИЗАЦИИ (`APP_TZ`, та же строка, что у бэкенда) — зона календарных
// суток: «сегодня», «просрочено», «за период» и срок-как-обязательство обязаны
// значить одно и то же для всех, иначе экран, отчёт и алерт разъезжаются.
//
// Зона МОМЕНТА («когда началось», «когда пришло») — зона устройства того, кто
// смотрит или вводит. Настройки зоны в профиле нет: люди летают, а выставленное
// руками значение протухает молча — ровно как протухала одна общая зона.
export const APP_TZ: string = runtimeConfig.APP_TZ

// Час, которым продукт считает срок «на весь день». Это то же значение `.env`,
// что бэкенд читает как `NOTIFIER_DEADLINE_HOUR`: именно в этот момент уходит
// «дедлайн наступил», и если UI считал бы конец суток, тон карточки расходился бы
// с алертом. Час настраиваемый, поэтому приезжает рантайм-конфигом, а не живёт
// литералом: смена часа в `.env` иначе развела бы воркер и экран.
export const ALL_DAY_DUE_TIME: string = runtimeConfig.ALL_DAY_DUE_TIME

// Зона момента — зона устройства, и это вся логика: она едет за человеком сама,
// синхронно и без единого его действия. Функция, а не константа: вкладка живёт
// дольше перелёта, а тест обязан уметь подменить зону.
export function momentZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || APP_TZ
}

export interface ZoneParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

const partsFmtCache = new Map<string, Intl.DateTimeFormat>()

function partsFmt(tz: string): Intl.DateTimeFormat {
  const cached = partsFmtCache.get(tz)
  if (cached) return cached
  // en-CA + 24-часовой цикл: формат предсказуемый, а нам нужны только числа.
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  partsFmtCache.set(tz, fmt)
  return fmt
}

// Календарные поля момента в зоне: «который час там». Дефолт — зона устройства:
// момент показывают и вводят по своим часам. Календарные сутки зовут эту функцию
// с явным `APP_TZ`.
export function zoneParts(date: Date, tz: string = momentZone()): ZoneParts {
  const parts = partsFmt(tz).formatToParts(date)
  const num = (type: Intl.DateTimeFormatPartTypes): number => {
    const value = parts.find((p) => p.type === type)?.value ?? '0'
    return Number(value)
  }
  return {
    year: num('year'),
    month: num('month'),
    day: num('day'),
    hour: num('hour'),
    minute: num('minute'),
    second: num('second'),
  }
}

// Смещение зоны в конкретный момент, мс. Считается через сами календарные поля:
// готового API у Intl нет, а зашивать константу нельзя — перевод стрелок.
export function zoneOffsetMs(date: Date, tz: string = momentZone()): number {
  const p = zoneParts(date, tz)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return asUtc - Math.floor(date.getTime() / 1000) * 1000
}

// Момент по «настенным» полям зоны — захват: цифры превращаются в инстант. Чужая
// зона здесь не ошибка показа, а записанное враньё, поэтому дефолт — зона
// устройства: «15:00», набранное в Челябинске, значит 15:00 в Челябинске.
// Обязательство студии (срок, напоминание к сроку) зовёт эту функцию с явным
// `APP_TZ`. Смещение уточняется вторым проходом: в сутки перевода стрелок первая
// догадка попадает не в ту сторону границы.
export function zonedMoment(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  tz: string = momentZone(),
): Date {
  const guess = Date.UTC(year, month - 1, day, hour, minute)
  const first = zoneOffsetMs(new Date(guess), tz)
  const second = zoneOffsetMs(new Date(guess - first), tz)
  return new Date(guess - second)
}

// `YYYY-MM-DD` → числа. Пустая/битая строка даёт null: день на проводе — строка
// строгого формата, и молча подставлять «сегодня» нельзя.
export function parseDay(day: string): { year: number; month: number; date: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  if (!m?.[1] || !m[2] || !m[3]) return null
  return { year: Number(m[1]), month: Number(m[2]), date: Number(m[3]) }
}

// `HH:MM` → числа; всё остальное (в том числе пустая строка) — null.
export function parseWallTime(time: string): { hour: number; minute: number } | null {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time)
  if (!m?.[1] || !m[2]) return null
  return { hour: Number(m[1]), minute: Number(m[2]) }
}

// День, на который приходится момент: `YYYY-MM-DD`. Дефолт — зона организации:
// «какое сегодня число» в продукте одно на всех, иначе сводка и отчёт у людей в
// разных поясах разъезжаются на сутки.
export function dayOf(date: Date, tz: string = APP_TZ): string {
  const p = zoneParts(date, tz)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`
}

// Момент дня и времени суток. Дефолт — зона организации: так собирается срок, а
// «до 8 августа 17:00» — обязательство студии и значит одно и то же в Москве и в
// Челябинске. Битый день — null: считать его «сегодня» значило бы соврать про срок.
export function momentOf(day: string, time?: string | null, tz: string = APP_TZ): Date | null {
  const d = parseDay(day)
  if (!d) return null
  const w = time ? parseWallTime(time) : null
  return zonedMoment(d.year, d.month, d.date, w?.hour ?? 0, w?.minute ?? 0, tz)
}

// Сегодняшний день — в зоне организации: границы периода общие для всех.
export function todayInZone(now: Date = new Date(), tz: string = APP_TZ): string {
  return dayOf(now, tz)
}

// Сдвиг календарного дня: считается по календарю (UTC-полдень как якорь), а не
// прибавлением 24 часов — иначе в сутки перевода стрелок день промахивается.
export function addDays(day: string, delta: number): string {
  const d = parseDay(day)
  if (!d) return day
  const anchor = new Date(Date.UTC(d.year, d.month - 1, d.date + delta))
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${anchor.getUTCFullYear()}-${pad(anchor.getUTCMonth() + 1)}-${pad(anchor.getUTCDate())}`
}

export interface ZoneInfo {
  tz: string
  // Короткое имя зоны глазами человека: «Москва», «Япония».
  name: string
  // Смещение строкой: «UTC+3», «UTC-4», «UTC+05:30».
  offset: string
  // Тег рядом со значением и в заголовке колонки — «Москва». Короткая форма той
  // же подписи, что стоит в шапке, поэтому читается как её сокращение. У зоны
  // без человеческого имени вырождается в смещение («UTC+0»).
  short: string
  // Подпись целиком — то, что стоит в бейдже: «Москва · UTC+3».
  label: string
  // Зона устройства и признак расхождения с подписываемой зоной: срок студии
  // «17:00» — не 17:00 на часах того, кто сидит в другом поясе, и об этом надо
  // сказать. У подписи момента расхождения не бывает: момент и показан по
  // устройству.
  browserTz: string
  mismatch: boolean
}

function offsetLabel(date: Date, tz: string): string {
  const total = Math.round(zoneOffsetMs(date, tz) / 60_000)
  const sign = total < 0 ? '-' : '+'
  const abs = Math.abs(total)
  const hours = Math.floor(abs / 60)
  const minutes = abs % 60
  return minutes === 0
    ? `UTC${sign}${hours}`
    : `UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

function zoneName(date: Date, tz: string): string {
  // Именно `longGeneric`, а не `shortGeneric`: короткая форма локализована не во
  // всех движках — Node отдаёт «Москва», а Chromium «Moscow» латиницей посреди
  // русского интерфейса (проверено в живом браузере; юнит-тесты под Node этого
  // не ловят). Длинная локализована везде, а её первая часть до запятой и есть
  // короткое имя: «Москва, стандартное время» → «Москва».
  const parts = new Intl.DateTimeFormat('ru-RU', {
    timeZone: tz,
    timeZoneName: 'longGeneric',
  }).formatToParts(date)
  const name =
    parts
      .find((p) => p.type === 'timeZoneName')
      ?.value.split(',')[0]
      ?.trim() ?? ''
  // У зон без человеческого имени Intl отдаёт то же смещение («GMT+03:00») —
  // тогда подпись вырождается в одно смещение, дублировать его незачем.
  return name === '' || /^(GMT|UTC)/.test(name) ? '' : name
}

// Подпись зоны для UI. Считается через Intl, а не зашитым «МСК»: зашитое соврало
// бы на первой же смене зоны организации и на любом устройстве не из Москвы.
// Дефолт — зона организации: подписывать нужно её, потому что она не совпадает с
// часами смотрящего; для подписи момента есть `momentZoneInfo`.
export function zoneInfo(now: Date = new Date(), tz: string = APP_TZ): ZoneInfo {
  const name = zoneName(now, tz)
  const offset = offsetLabel(now, tz)
  const browserTz = momentZone()
  return {
    tz,
    name,
    offset,
    short: name === '' ? offset : name,
    label: name === '' ? offset : `${name} · ${offset}`,
    browserTz,
    // Сравниваем смещения, а не имена зон: Europe/Kyiv и Europe/Berlin летом
    // показывают одно и то же время, и дёргать человека там не за чем.
    mismatch: browserTz !== tz && zoneOffsetMs(now, browserTz) !== zoneOffsetMs(now, tz),
  }
}

// Полная форма подписи зоны организации — `title` у срока, периода и всего, что
// из срока выведено. Говорит вслух то, что короткий тег только называет: почему
// эта зона, а не ваша, и чем она от ваших часов отличается.
export function zoneTitle(now: Date = new Date(), tz: string = APP_TZ): string {
  const zone = zoneInfo(now, tz)
  const base = `Сутки и сроки считаются в зоне организации (${zone.tz}): они одни на всех`
  return zone.mismatch
    ? `${base}. Ваше устройство — ${zone.browserTz}; моменты («создано», «начато») показаны по нему.`
    : `${base}.`
}

// Подпись зоны момента — зоны устройства. Отдельная функция, а не `zoneInfo` с
// аргументом: подпись обязана называть зону так, как она выбрана, иначе экран
// снова врёт про захват.
export function momentZoneInfo(now: Date = new Date()): ZoneInfo {
  return zoneInfo(now, momentZone())
}

// Полная форма подписи момента: показ идёт по часам смотрящего, а зона
// организации остаётся у суток и сроков — обе половины названы, чтобы «17:00»
// на карточке срока и «17:00» в списке интервалов не читались как одно и то же.
export function momentZoneTitle(): string {
  const tz = momentZone()
  const base = `Момент показан в зоне вашего устройства (${tz})`
  return tz === APP_TZ ? `${base}.` : `${base}. Сутки и сроки — в зоне организации (${APP_TZ}).`
}
